import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Lock, Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard({ open, setOpen }) {
  const [secret, setSecret] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- 1. DEFINE FETCHDATA FIRST (Wrapped in useCallback) ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await api.adminList(secret);

      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else {
        throw new Error("Failed to fetch data");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load data. Please try again.");
      toast.error("Failed to fetch waitlist data");
    } finally {
      setLoading(false);
    }
  }, [secret]); // Dependency: Re-create function only if 'secret' changes

  // --- 2. USEEFFECT NOW INCLUDES FETCHDATA ---
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]); // Fixed: Added fetchData dependency

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsAuthenticated(false);
      setSecret("");
      setUsers([]);
      setError(null);
    }
  }, [open]);

  const handleLogin = async () => {
    if (!secret.trim()) {
      toast.error("Please enter admin secret");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await api.adminAuth(secret);

      if (res.ok) {
        setIsAuthenticated(true);
        toast.success("Access granted");
        // Note: We don't need to call fetchData() here because 
        // setIsAuthenticated(true) triggers the useEffect above.
      } else {
        toast.error("Invalid admin secret");
        setSecret("");
      }
    } catch (err) {
      console.error(err);
      toast.error("Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.adminExport(secret);

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `faast-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("CSV downloaded successfully");
      } else {
        toast.error("Failed to download CSV");
      }
    } catch (err) {
      console.error(err);
      toast.error("Download failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="premium-scroll max-w-4xl max-h-[85vh] overflow-y-auto bg-[#0B1220]/95 border-white/10 text-white backdrop-blur-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl md:text-2xl">
            <Lock className="h-5 w-5 text-[#FACC15]" />
            Admin Dashboard
          </DialogTitle>
        </DialogHeader>

        {!isAuthenticated ? (
          <div className="flex flex-col gap-4 py-8">
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Enter Admin Secret"
                className="h-12 rounded-xl bg-white/5 border-white/10 text-white focus-visible:ring-[#FACC15]"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleLogin()}
              />
              <p className="text-xs text-white/50">
                Access restricted to authorized personnel only
              </p>
            </div>
            <Button 
              onClick={handleLogin} 
              disabled={loading}
              className="h-12 rounded-xl bg-[#FACC15] text-[#0B1220] hover:bg-[#F7C512] disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Authenticating...
                </>
              ) : (
                "Access Dashboard"
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <p className="text-white/60">
                  Total: <span className="font-semibold text-white">{users.length}</span> users
                </p>
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-green-500"></span>
                  <span className="text-xs text-white/50">Live</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={fetchData} 
                  variant="outline" 
                  disabled={loading}
                  className="h-10 rounded-xl border-white/10 bg-transparent text-white hover:bg-white/5"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button 
                  onClick={downloadCsv} 
                  variant="outline" 
                  className="h-10 rounded-xl border-white/10 bg-transparent text-white hover:bg-white/5"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              </div>
            )}

            {loading && !users.length ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#FACC15]" />
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-white/50">No users in waitlist yet</p>
              </div>
            ) : (
              <div className="rounded-xl border border-white/10 overflow-x-auto">
                <Table>
                  <TableHeader className="bg-white/5">
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white font-semibold whitespace-nowrap">Name</TableHead>
                      <TableHead className="text-white font-semibold whitespace-nowrap">Phone</TableHead>
                      <TableHead className="text-white font-semibold whitespace-nowrap">Email</TableHead>
                      <TableHead className="text-white font-semibold whitespace-nowrap">City</TableHead>
                      <TableHead className="text-white font-semibold whitespace-nowrap">Role</TableHead>
                      <TableHead className="text-right text-white font-semibold whitespace-nowrap">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user, idx) => (
                      <TableRow 
                        key={user.phone || idx} 
                        className="border-white/10 transition-colors hover:bg-white/5"
                      >
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="font-mono text-sm">+91 {user.phone}</TableCell>
                        <TableCell className="text-sm text-white/70">{user.email}</TableCell>
                        <TableCell>{user.city}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            user.role === 'driver' 
                              ? 'bg-[#FACC15]/20 text-[#FACC15]' 
                              : 'bg-white/10 text-white'
                          }`}>
                            {user.role === 'driver' ? 'Pilot' : 'Rider'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-white/50 text-xs">
                          {new Date(user.timestamp).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}